/**
 * SectionNewsletter — "The Weekly Load" inline signup for the FAQ page.
 *
 * A REAL working form: role toggle (renter/owner) → email → POST to
 * /api/newsletter/subscribe with { email, audience, source } and a success
 * state. Self-contained inline styles (light "Weekly Load" card family —
 * same visual system as the popup: warm off-white card, real logo PNG,
 * condensed headline, checkmark benefits, navy fine-print band, no emoji).
 */

import React, { useState } from 'react';

import logoImg from '../../assets/newsletter/weekly-load-logo.png';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const SOURCE = 'weekly-load-faq';

const C = {
  paper: '#F6F4EF',
  navy: '#0E1B2A',
  blue: '#2F7FE0',
  blueDark: '#1E5FB0',
  red: '#B02419',
  redDark: '#8E1D14',
  ink: '#1A1A1A',
  muted: '#5A6B7A',
  green: '#2E9E6B',
};

const CONDENSED =
  "'Oswald', 'Archivo Narrow', 'Arial Narrow', 'Franklin Gothic Medium', Impact, system-ui, sans-serif";

/* ------------------------------ inline icons ------------------------------ */

const TrailerIcon = ({ color = '#fff', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="8" width="15" height="7" rx="1" />
    <path d="M17 12h5" />
    <circle cx="7" cy="17.5" r="1.8" />
    <circle cx="12.5" cy="17.5" r="1.8" />
  </svg>
);

const TagIcon = ({ color = '#fff', size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.6 13.4L12 22l-8.6-8.6a2 2 0 0 1-.6-1.4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 1.4.6l6.4 6.4a2 2 0 0 1 0 2.8z" transform="rotate(90 12 12)" />
    <circle cx="9" cy="9" r="1.6" fill={color} stroke="none" />
  </svg>
);

const LockIcon = ({ color = '#fff', size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const CheckCircleIcon = ({ color = C.blue, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <circle cx="12" cy="12" r="11" />
    <path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const BENEFITS = {
  renter: [
    'New trailers listed near you',
    'What to check before you rent',
    'Towing & hauling tips that help',
  ],
  owner: [
    'What owners are earning',
    'Get more renters (we do the finding)',
    'Listing tips that get inquiries',
  ],
};

function SectionNewsletter() {
  const [role, setRole] = useState('renter'); // 'renter' | 'owner'
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const emailInvalid = touched && !EMAIL_RE.test(email.trim());
  const accent = role === 'owner' ? C.red : C.blue;

  const submit = async e => {
    e.preventDefault();
    setTouched(true);
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setError('Please enter a valid email.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: clean,
          audience: role,
          source: SOURCE,
          list: 'bigloads-weekly-load',
        }),
      });
      if (res.ok || res.status === 202) {
        setDone(true);
      } else if (res.status === 400) {
        setError('Please enter a valid email.');
      } else {
        setError("Hmm, that didn't go through. Try again in a moment.");
      }
    } catch (err) {
      setError("Couldn't reach the server. Give it another shot.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.wrap} className="wlf-wrap">
      <div style={styles.card}>
        <div style={styles.inner}>
          <img src={logoImg} alt="The Weekly Load" style={styles.logo} />
          {done ? (
            <div style={styles.center}>
              <div style={styles.check}>
                <CheckCircleIcon color={C.green} size={52} />
              </div>
              <h2 style={styles.h2}>YOU'RE ON THE LIST</h2>
              <p style={styles.sub}>
                Look out for your first <strong>Weekly Load</strong> soon — trailer
                tips, deals, and listings near you.
              </p>
            </div>
          ) : (
            <>
              <h2 style={styles.h2}>
                <span style={{ color: C.ink }}>GET THE </span>
                <span style={{ color: C.red }}>WEEKLY LOAD</span>
              </h2>
              <p style={styles.sub}>
                The best trailer tips, deals, and listings near you — once a week,
                no spam.
              </p>

              <div style={styles.roleRow} className="wlf-roles">
                <button
                  type="button"
                  onClick={() => setRole('renter')}
                  style={{
                    ...styles.roleBtn,
                    ...(role === 'renter'
                      ? { background: C.blue, borderColor: C.blue, color: '#fff' }
                      : styles.roleIdle),
                  }}
                >
                  <TrailerIcon color={role === 'renter' ? '#fff' : C.muted} />
                  I'M RENTING
                </button>
                <button
                  type="button"
                  onClick={() => setRole('owner')}
                  style={{
                    ...styles.roleBtn,
                    ...(role === 'owner'
                      ? { background: C.red, borderColor: C.red, color: '#fff' }
                      : styles.roleIdle),
                  }}
                >
                  <TagIcon color={role === 'owner' ? '#fff' : C.muted} />
                  I OWN / WANT TO LIST
                </button>
              </div>

              <form style={styles.form} className="wlf-form" onSubmit={submit} noValidate>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="Enter your email address"
                  value={email}
                  disabled={submitting}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  onBlur={() => setTouched(true)}
                  style={{
                    ...styles.input,
                    ...(emailInvalid || error ? styles.inputError : {}),
                  }}
                  aria-label="Email address"
                  aria-invalid={emailInvalid || !!error}
                />
                <button
                  type="submit"
                  style={{ ...styles.cta, background: accent }}
                  disabled={submitting}
                >
                  {submitting ? 'SENDING…' : 'SEND IT MY WAY'}
                </button>
              </form>
              {error ? <div style={styles.errorText}>{error}</div> : null}

              <div style={styles.benefits} className="wlf-benefits">
                {BENEFITS[role].map(b => (
                  <div style={styles.benefitItem} key={b}>
                    <CheckCircleIcon color={accent} />
                    <span style={styles.benefitText}>{b}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={styles.footerBand}>
          <span style={styles.footerLockWrap}>
            <LockIcon />
            <span style={styles.footerFine}>One email a week. Unsubscribe anytime.</span>
          </span>
          <a href="/privacy-policy" style={styles.footerLink}>Privacy Policy</a>
        </div>
      </div>

      <style>{`
        @media (max-width: 560px) {
          .wlf-wrap .wlf-roles { flex-direction: column !important; }
          .wlf-wrap .wlf-form { flex-direction: column !important; }
          .wlf-wrap .wlf-benefits { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  wrap: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 16px 8px',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    boxSizing: 'border-box',
  },
  card: {
    width: 'min(620px, 100%)',
    background: C.paper,
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 16px 44px rgba(0,0,0,0.16)',
    color: C.ink,
  },
  inner: { padding: '26px 28px 22px' },
  logo: { width: '150px', height: 'auto', display: 'block', marginBottom: '14px' },
  h2: {
    margin: '0 0 6px',
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '30px',
    lineHeight: 1.05,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  sub: { margin: '0 0 16px', fontSize: '15px', lineHeight: 1.5, color: C.muted, fontWeight: 500 },
  roleRow: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' },
  roleBtn: {
    flex: '1 1 200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '2px solid',
    fontFamily: CONDENSED,
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.12s ease',
  },
  roleIdle: { background: '#fff', borderColor: '#C9C4B8', color: C.muted },
  form: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
  input: {
    flex: '1 1 240px',
    boxSizing: 'border-box',
    background: '#fff',
    color: C.ink,
    border: '1px solid #C9C4B8',
    borderRadius: '8px',
    padding: '14px 15px',
    fontSize: '15px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  inputError: { border: `1px solid ${C.red}`, background: 'rgba(176,36,25,0.05)' },
  cta: {
    flex: '0 0 auto',
    padding: '14px 22px',
    borderRadius: '8px',
    border: 'none',
    color: '#fff',
    fontFamily: CONDENSED,
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
    whiteSpace: 'nowrap',
  },
  errorText: { color: C.red, fontSize: '13px', margin: '8px 2px 0', fontWeight: 600 },
  benefits: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px',
    marginTop: '16px',
  },
  benefitItem: { display: 'flex', alignItems: 'flex-start', gap: '8px' },
  benefitText: { fontSize: '12.5px', lineHeight: 1.35, color: C.ink, fontWeight: 600 },
  footerBand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    flexWrap: 'wrap',
    padding: '11px 20px',
    background: C.navy,
  },
  footerLockWrap: { display: 'inline-flex', alignItems: 'center', gap: '8px' },
  footerFine: { fontSize: '12px', color: '#fff' },
  footerLink: { fontSize: '12px', color: '#fff', textDecoration: 'underline', whiteSpace: 'nowrap' },
  center: { textAlign: 'center', padding: '6px 0 2px' },
  check: { display: 'flex', justifyContent: 'center', margin: '0 auto 12px' },
};

export default SectionNewsletter;
